import type { CatalogImportRecord } from '@/api/models';
import { countParam } from '@/pages/catalogImport/notices';
import { formatGel } from '@/utils/money';

/**
 * The import's counts, in the card's order. Drafts (the products it
 * imported as drafts, bos-app#221) shows only when there are any.
 */
export const IMPORT_COUNT_FIGURES = [
    'products',
    'drafts',
    'variants',
    'batches',
    'units',
] as const;

export type ImportCountFigure = (typeof IMPORT_COUNT_FIGURES)[number];

/** The import's amounts, in the card's order. */
export const IMPORT_MONEY_FIGURES = [
    'netCost',
    'vatExcluded',
    'saleValue',
] as const;

export type ImportMoneyFigure = (typeof IMPORT_MONEY_FIGURES)[number];

export type ImportFigureKey = ImportCountFigure | ImportMoneyFigure;

/** What the import created, in the card's order. */
export const IMPORT_CREATED_KINDS = [
    'brands',
    'categories',
    'suppliers',
    'pendingValues',
] as const;

export type ImportCreatedKind = (typeof IMPORT_CREATED_KINDS)[number];

const COUNT_FIELD = {
    products: 'productsCreated',
    drafts: 'productsDrafted',
    variants: 'variantsCreated',
    batches: 'batchesCreated',
    units: 'units',
} as const satisfies Record<ImportCountFigure, keyof CatalogImportRecord>;

/** Each amount's tetri twin: money is printed from integer tetri. */
const MONEY_FIELD = {
    netCost: 'netCostTetri',
    vatExcluded: 'vatExcludedTetri',
    saleValue: 'saleValueTetri',
} as const satisfies Record<ImportMoneyFigure, keyof CatalogImportRecord>;

const CREATED_FIELD = {
    brands: 'brandsCreated',
    categories: 'categoriesCreated',
    suppliers: 'suppliersCreated',
    pendingValues: 'pendingValuesCreated',
} as const satisfies Record<ImportCreatedKind, keyof CatalogImportRecord>;

// Literal keys, so the locale guards see every one of them.
export const IMPORT_FIGURE_LABEL_KEY = {
    products: 'catalogImport.result.stats.figures.products',
    drafts: 'catalogImport.result.stats.figures.drafts',
    variants: 'catalogImport.result.stats.figures.variants',
    batches: 'catalogImport.result.stats.figures.batches',
    units: 'catalogImport.result.stats.figures.units',
    netCost: 'catalogImport.result.stats.figures.netCost',
    vatExcluded: 'catalogImport.result.stats.figures.vatExcluded',
    saleValue: 'catalogImport.result.stats.figures.saleValue',
} as const satisfies Record<ImportFigureKey, string>;

/** Plural keys: each takes `count`, and the count as printed in `total`. */
export const IMPORT_CREATED_LABEL_KEY = {
    brands: 'catalogImport.result.stats.created.brands',
    categories: 'catalogImport.result.stats.created.categories',
    suppliers: 'catalogImport.result.stats.created.suppliers',
    pendingValues: 'catalogImport.result.stats.created.pendingValues',
} as const satisfies Record<ImportCreatedKind, string>;

/**
 * One figure of the card: its number (a count, or an amount in tetri) and
 * how the app prints it, a count grouped and an amount as money.
 */
export type ImportFigure = {
    key: ImportFigureKey;
    value: number;
    text: string;
};

export type ImportCreated = { kind: ImportCreatedKind; count: number };

/**
 * The opening-stock entry the import posted, or none: an import whose stock
 * costs nothing (no units, or no cost) posts nothing.
 */
export type ImportPosting =
    | {
          posted: true;
          /** The entry's amount: the stock's net value, as money. */
          amount: string;
      }
    | { posted: false };

/**
 * Products created, the products imported as drafts (left out at 0),
 * variants and batches created, and the units received.
 */
export const importCountsOf = (record: CatalogImportRecord): ImportFigure[] =>
    IMPORT_COUNT_FIGURES.map((key) => {
        const value = record[COUNT_FIELD[key]];
        return { key, value, text: countParam(value) };
    }).filter(({ key, value }) => key !== 'drafts' || value > 0);

/** The stock's net value, the VAT taken out of its costs and its sale value. */
export const importAmountsOf = (record: CatalogImportRecord): ImportFigure[] =>
    IMPORT_MONEY_FIGURES.map((key) => {
        const value = record[MONEY_FIELD[key]];
        return { key, value, text: formatGel(value) };
    });

/** The names the import created; a kind it created none of is left out. */
export const importCreatedOf = (record: CatalogImportRecord): ImportCreated[] =>
    IMPORT_CREATED_KINDS.map((kind) => ({
        kind,
        count: record[CREATED_FIELD[kind]],
    })).filter(({ count }) => count > 0);

/**
 * The entry behind the posting line. It debits inventory with the stock's
 * net value, which the record carries as `netCost`.
 */
export const importPostingOf = (record: CatalogImportRecord): ImportPosting =>
    record.journalEntryId === null
        ? { posted: false }
        : { posted: true, amount: formatGel(record.netCostTetri) };
